import React, { useEffect, useState, useRef, useCallback } from 'react';

// 키워드 클라우드 컴포넌트 (vegaEmbed 사용)
const KeywordCloud = ({ onRenderComplete, isVisible = true }) => {
    const [error, setError] = useState(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 300 });
    const [copyFeedback, setCopyFeedback] = useState({ show: false, text: '' });
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const [serverChartData, setServerChartData] = useState(null);

    // 텍스트를 클립보드에 복사하는 함수
    const copyTextToClipboard = async (text) => {
        let originalText = text;

        // 괄호 안 숫자 제거 (예: "키워드 (10)" -> "키워드")
        if (text.includes(' (')) {
            originalText = text.split(' (')[0];
        }

        try {
            // 최신 클립보드 API 사용 시도
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(originalText);
                showCopyFeedback(originalText);
                return;
            }

            // 폴백: 대체 방법 (주로 모바일 및 오래된 브라우저용)
            const textArea = document.createElement('textarea');
            textArea.value = originalText;
            textArea.style.position = 'fixed';  // 화면 밖에 위치
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (successful) {
                showCopyFeedback(originalText);
            } else {
                console.error('클립보드 복사 실패');
            }
        } catch (err) {
            console.error('클립보드 복사 중 오류:', err);
        }
    };

    // 복사 성공 피드백 표시
    const showCopyFeedback = (text) => {
        setCopyFeedback({ show: true, text: `"${text}" 복사됨` });

        // 2초 후 피드백 메시지 숨김
        setTimeout(() => {
            setCopyFeedback({ show: false, text: '' });
        }, 2000);
    };

    // 차트 렌더링 함수 (리사이즈 이벤트에서 재사용하기 위해 useCallback으로 메모이제이션)
    const renderChart = useCallback(async (vegaSpec) => {
        if (!containerRef.current || !window.vegaEmbed) return;

        try {
            // 이전 차트가 있으면 제거
            if (chartRef.current) {
                chartRef.current.finalize();
            }

            // 새 차트 렌더링
            const result = await window.vegaEmbed(containerRef.current, vegaSpec, {
                mode: "vega",
                renderer: "svg",
                actions: false
            });
            chartRef.current = result.view;

            // 클릭 이벤트 리스너 추가
            chartRef.current.addEventListener('click', function (event, item) {
                if (item && item.datum) {
                    copyTextToClipboard(item.datum.text);
                }
            });

            if (onRenderComplete) onRenderComplete();
        } catch (err) {
            console.error('Vega 렌더링 오류:', err);
            setError(`워드 클라우드 렌더링 오류: ${err.message}`);
            if (onRenderComplete) onRenderComplete();
        }
    }, [onRenderComplete]);

    // 컨테이너 크기 측정 및 업데이트
    const updateDimensions = useCallback(() => {
        if (containerRef.current) {
            const { width } = containerRef.current.getBoundingClientRect();
            // 너비가 실제로 변경된 경우만 상태 업데이트
            if (width !== dimensions.width) {
                setDimensions({
                    width: Math.max(width, 300), // 최소 너비 300px 보장
                    height: 300
                });
            }
        }
    }, [dimensions.width]);

    // Vega 스크립트 로딩 및 초기 설정
    useEffect(() => {
        // 콘솔 경고 메시지 억제
        const originalConsoleWarn = console.warn;
        console.warn = function (message, ...args) {
            if (typeof message === 'string' && message.includes('v7_startTransition')) {
                return; // 이 경고는 무시
            } else if (typeof message === 'string' && message.includes('v7_relativeSplatPath')) {
                return; // 이 경고는 무시
            }
            // 다른 경고는 정상적으로 출력
            return originalConsoleWarn.call(this, message, ...args);
        };

        // 스크립트 로딩 상태 추적
        const loadScript = (url, callback) => {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = url;
            script.onload = callback;
            script.onerror = () => setError(`스크립트 로딩 실패: ${url}`);
            document.head.appendChild(script);
            return script;
        };

        // 필요한 스크립트 로딩
        const vegaScript = loadScript('https://cdn.jsdelivr.net/npm/vega@6.1.2', () => {
            loadScript('https://cdn.jsdelivr.net/npm/vega-embed@7.0.1', () => {
                // 초기 컨테이너 크기 측정
                updateDimensions();
                fetchDataAndRenderChart();
            });
        });

        // 컴포넌트 언마운트 시 정리
        return () => {
            // 콘솔 경고 함수 복원
            console.warn = originalConsoleWarn;

            // 차트 뷰 정리
            if (chartRef.current) {
                chartRef.current.finalize();
            }

            // 로드된 스크립트 제거 (선택적)
            if (vegaScript && vegaScript.parentNode) {
                vegaScript.parentNode.removeChild(vegaScript);
            }
        };
    }, [updateDimensions]);

    // 리사이즈 핸들러 및 ResizeObserver 설정
    useEffect(() => {
        // 창 크기 변경 이벤트 핸들러
        const handleResize = () => {
            updateDimensions();
        };

        // ResizeObserver 설정
        let resizeObserver;
        if (containerRef.current && window.ResizeObserver) {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(containerRef.current);
        }

        // 윈도우 리사이즈 이벤트 리스너
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [updateDimensions]);

    // 차원 변경 시 차트 다시 렌더링
    useEffect(() => {
        if (dimensions.width > 0 && isVisible) {
            fetchDataAndRenderChart();
        }
    }, [isVisible]); // isVisible 의존성 추가

    const fetchDataAndRenderChart = async () => {
        if (!containerRef.current || dimensions.width === 0 || !isVisible) return;
        try {
            // 샘플 데이터 (API가 실패할 경우 대비)
            const sampleData = [
                { text: "Agent", value: 19 },
                { text: "AI", value: 11 },
                { text: "LLM", value: 15 },
                { text: "Quantum", value: 7 },
                { text: "VLM", value: 4 },
                { text: "responsible ai", value: 4 },
                { text: "alignment", value: 4 },
                { text: "Reasoning", value: 5 },
                { text: "multimodal", value: 3 },
                { text: "HCI", value: 3 },
                { text: "MCP", value: 3 },
                { text: "efficient inference", value: 2 },
                { text: "RAG", value: 2 },
                { text: "cot", value: 2 },
                { text: "omni", value: 2 },
                { text: "Diffusion", value: 2 },
                { text: "reinforcement learn", value: 2 },
                { text: "transformer", value: 2 },
                { text: "multi-agent", value: 2 },
                { text: "VLA", value: 2 },
                { text: "evaluation", value: 2 }
            ];

            let keywordData;

            if (!serverChartData) {

                // API에서 데이터 가져오기
                try {
                    const response = await fetch('/api/keywords/cloud');
                    if (response.ok) {
                        const data = await response.json();
                        if (data && data.keywords && Array.isArray(data.keywords) && data.keywords.length > 0) {
                            // 데이터 값 증폭 (시각화를 위해)
                            keywordData = data.keywords.map(item => ({
                                text: item.text,  // 원본 텍스트 (복사용)
                                // displayText: `${item.text} (${item.value})`, // 표시용 텍스트
                                displayText: item.text, // 표시용 텍스트
                                value: item.value  // 값을 증폭
                                // value: item.value * 40 + 30  // 값을 증폭
                            }));
                        }
                    }
                } catch (error) {
                    console.warn('API 오류:', error);
                }
                if (keywordData) {
                    setServerChartData(keywordData);
                } else {
                    // API 실패 시 샘플 데이터 사용
                    keywordData = sampleData.map(item => ({
                        text: item.text,
                        // displayText: `${item.text} (${item.value})`,
                        displayText: item.text, // 표시용 텍스트
                        value: item.value
                    }));
                }
            } else {
                // 서버에서 가져온 데이터 사용
                keywordData = serverChartData.map(item => item);
            }

            // Vega 스펙 정의 - 동적 크기 적용
            // 통계값 계산
            const values = keywordData.map(item => item.value);
            const mean = values.reduce((a, b) => a + b, 0) / values.length;
            const variance = values.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / values.length;
            const std = Math.sqrt(variance);

            // Z-score 기반 정규화 함수
            const normalizeValue = (value) => {
                if (std === 0) return 0.5; // 모든 값이 같은 경우

                // Z-score 계산
                const zScore = (value - mean) / std;

                // -1.5σ ~ +1.5σ 범위로 클리핑
                const clippedZ = Math.max(-1.5, Math.min(1.5, zScore));

                // 0-1 범위로 정규화 (-1.5 → 0, +1.5 → 1)
                return (clippedZ + 1.5) / 3.0;
            };
            const spec = {
                "$schema": "https://vega.github.io/schema/vega/v6.json", // v5에서 v6으로 업데이트
                "description": "Popular keywords word cloud",
                "width": dimensions.width,
                "height": dimensions.height,
                "padding": 0,

                "data": [
                    {
                        "name": "table",
                        "values": keywordData.map(item => ({
                            text: item.text,          // 원본 텍스트 (복사용)
                            displayText: item.displayText, // 표시용 텍스트
                            count: normalizeValue(item.value) // 정규화된 0-1 값
                        })),
                        "transform": [
                            {
                                "type": "formula",
                                "as": "angle",
                                "expr": "[-45, 0, 45][~~(random() * 3)]"
                            },
                            {
                                "type": "formula",
                                "as": "weight",
                                "expr": "200 + 250 * datum.count" // 150-400 범위 (250 = 400-150)
                            }
                        ]
                    }
                ],

                "scales": [
                    {
                        "name": "color",
                        "type": "ordinal",
                        "domain": { "data": "table", "field": "text" },
                        "range": [
                            "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
                            "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"
                        ]
                    }
                ],

                "marks": [
                    {
                        "type": "text",
                        "from": { "data": "table" },
                        "encode": {
                            "enter": {
                                "text": { "field": "displayText" }, // 표시용 텍스트 사용
                                "align": { "value": "center" },
                                "baseline": { "value": "alphabetic" },
                                "fill": { "scale": "color", "field": "text" },
                                "cursor": { "value": "pointer" } // 커서를 포인터로 변경하여 클릭 가능함을 표시
                            },
                            "update": { "fillOpacity": { "value": 1 } },
                            "hover": {
                                "fillOpacity": { "value": 0.7 }
                                // 글자 크기 변경 제거함 - 이게 글자가 사라지는 원인
                            }
                        },
                        "transform": [
                            {
                                "type": "wordcloud",
                                "size": [dimensions.width, dimensions.height],
                                "text": { "field": "datum.displayText" },
                                "rotate": { "field": "datum.angle" },
                                "font": "Helvetica Neue, Arial",
                                "fontSize": { "field": "datum.count" },
                                "fontWeight": { "field": "datum.weight" },
                                "fontSizeRange": [18, 56],
                                "padding": 2
                            }
                        ]
                    }
                ]
            };

            // 차트 렌더링
            await renderChart(spec);

        } catch (err) {
            console.error('Error fetching data:', err);
            setError(`데이터 불러오기 오류: ${err.message}`);
            if (onRenderComplete) onRenderComplete();
        }
    };

    return (
        <div className="w-full flex flex-col justify-center items-center relative">
            {error ? (
                <div className="text-center text-red-500 py-2">{error}</div>
            ) : (
                <div
                    ref={containerRef}
                    className="w-full h-[300px] flex justify-center items-center bg-white rounded-lg shadow"
                    style={{ minHeight: '300px' }}
                ></div>
            )}

            {/* 복사 피드백 메시지 */}
            {copyFeedback.show && (
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg text-sm animate-fade-in-out">
                    {copyFeedback.text}
                </div>
            )}
        </div>
    );
};

export default KeywordCloud;